import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { discounted } from '../../shared/contacts';
import { contactPerk } from '../contacts/perks';

/**
 * A word with the desk sergeant (docs/POLICE.md §13), offline only. Sergeant Alf Rudd keeps the front desk
 * at Low Borough nick and, like his DS, is not above a drink: ring the desk from any phone box while you're
 * wanted and, for a price, a report goes missing — the wanted level drops by one.
 *
 *  - price by level: £200 at level 1, £450 at 2, £800 at 3 (dearer than Pike's respray at the same level);
 *    at level 4 the whole nick is out and Holloway has the radio: nobody at the desk will touch it;
 *  - not while officers can see you at level 2 or more (break their line of sight first); at level 1 he can
 *    still call off the one bobby who's watching;
 *  - once per hour on the borough clock (a game hour is 90 s of play);
 *  - not on a job (the jobs' "lose the police" parts are the job), on foot at the box, offline only (online
 *    the server owns the wanted level and has no such handler).
 *
 * `bribeCheck` holds the rules (pure, tested); the system below puts the call on every phone box.
 */

export const SERGEANT = 'Sgt. Alf Rudd';
/** Price to take one level off, by the level you're at now (index 0 unused, level 4 refused). */
export const BRIBE_PRICE: readonly number[] = [0, 200, 450, 800];
/** Seconds of play in one hour on the borough clock (render/mood.ts CLOCK_HOURS_PER_SECOND = 1/90). */
export const GAME_HOUR_SECONDS = 90;
/** One favour per hour of game time. */
export const BRIBE_COOLDOWN = GAME_HOUR_SECONDS;
/** From this level on, being watched stops the call (at level 1 he calls the bobby off). */
export const BRIBE_SEEN_FROM = 2;

export type BribeRefusal = 'notWanted' | 'tooHot' | 'seen' | 'cooldown' | 'onJob' | 'broke' | 'online';

export interface BribeSituation {
  level: number;
  /** An officer can see the player right now. */
  seen: boolean;
  /** Sim seconds now, and when the next favour can be asked (0 = any time). */
  now: number;
  readyAt: number;
  money: number;
  onJob: boolean;
  online: boolean;
  /** Mags' word with the desk (contact ladder, WP-X1): a fraction off the price. */
  discount?: number;
}

export type BribeCheck = { ok: true; price: number } | { ok: false; reason: BribeRefusal; price: number | null };

/** What a level costs (null: not for sale at that level). */
export function bribePrice(level: number): number | null {
  const l = Math.round(level);
  return l >= 1 && l < BRIBE_PRICE.length ? BRIBE_PRICE[l]! : null;
}

export function bribeCheck(s: BribeSituation): BribeCheck {
  const base = bribePrice(s.level);
  const price = base === null || !s.discount ? base : discounted(base, s.discount);
  if (s.online) return { ok: false, reason: 'online', price };
  if (s.level <= 0) return { ok: false, reason: 'notWanted', price: null };
  if (price === null) return { ok: false, reason: 'tooHot', price: null };
  if (s.onJob) return { ok: false, reason: 'onJob', price };
  if (s.seen && s.level >= BRIBE_SEEN_FROM) return { ok: false, reason: 'seen', price };
  if (s.now < s.readyAt) return { ok: false, reason: 'cooldown', price };
  if (s.money < price) return { ok: false, reason: 'broke', price };
  return { ok: true, price };
}

/** Plain words for the toast when he says no. */
export function refusalText(c: Extract<BribeCheck, { ok: false }>, minutesLeft = 0): string {
  switch (c.reason) {
    case 'online':
      return 'The desk sergeant only takes calls offline.';
    case 'notWanted':
      return 'You’re not wanted. Nothing to make go away.';
    case 'tooHot':
      return 'At wanted level 4 the whole station is out and DS Holloway has the radio. Nobody at the desk can help — lose them yourself.';
    case 'onJob':
      return 'Not in the middle of a job. Lose the police the hard way.';
    case 'seen':
      return 'Not while the police can see you. Get out of their sight first, then ring.';
    case 'cooldown':
      return `He’s done you one favour this hour. Ring again in about ${Math.max(1, minutesLeft)} minute${minutesLeft > 1 ? 's' : ''}.`;
    case 'broke':
      return `He wants £${c.price} to lose the paperwork. You haven’t got it.`;
  }
}

/** The short prompt beside the phone box (full reasons come as a toast on pressing). */
function promptFor(c: BribeCheck): string {
  if (c.ok) return `Ring the desk sergeant — wanted level down one, £${c.price}`;
  switch (c.reason) {
    case 'seen':
      return 'Desk sergeant: not while you’re seen';
    case 'cooldown':
      return 'Desk sergeant: one favour an hour';
    case 'broke':
      return `Desk sergeant: £${c.price} — you’re short`;
    case 'tooHot':
      return 'Desk sergeant: too hot to touch';
    default:
      return 'Ring the desk sergeant';
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Ring the desk sergeant from wherever you are (tests): the result, as the phone box would give it. */
    bribeSergeant(): { ok: boolean; reason?: string; price?: number | null; level: number };
  }
}

export function createDeskSergeantSystem(): System {
  let ctx: GameContext;
  let readyAt = 0;
  const offs: (() => void)[] = [];

  const online = (): boolean => !!ctx.services.tryGet('net')?.online || !!ctx.services.tryGet('heat')?.serverAuthority;

  function check(): BribeCheck {
    const heat = ctx.services.tryGet('heat');
    return bribeCheck({
      level: ctx.state.heat,
      seen: heat?.seen ?? false,
      now: ctx.state.time,
      readyAt,
      money: ctx.services.tryGet('economy')?.balance ?? ctx.state.money,
      onJob: !!ctx.services.tryGet('missions')?.active,
      online: online(),
      discount: contactPerk(ctx, 'sergeant'),
    });
  }

  function ring(): BribeCheck {
    const c = check();
    const ui = ctx.services.tryGet('ui');
    if (!c.ok) {
      const mins = Math.ceil(((readyAt - ctx.state.time) / GAME_HOUR_SECONDS) * 60);
      ui?.toast(refusalText(c, mins), 'warn', 4);
      ctx.services.tryGet('audio')?.ui('denied');
      return c;
    }
    const eco = ctx.services.tryGet('economy');
    if (eco && !eco.spend(c.price, 'desk-sergeant')) return { ok: false, reason: 'broke', price: c.price };
    const before = ctx.state.heat;
    const after = ctx.services.get('heat').dropOne();
    readyAt = ctx.state.time + BRIBE_COOLDOWN;
    ctx.services.tryGet('audio')?.sfx('handset');
    ui?.say(`${SERGEANT} (phone)`, after === 0 ? 'Never heard of you, sunshine. Go home.' : 'Leave it with me. Paperwork goes walkabout all the time round here.', 3.5);
    ui?.toast(`Paid £${c.price}. Wanted level ${before} → ${after}. One favour an hour.`, 'good', 4);
    return c;
  }

  return {
    id: 'deskSergeant',
    order: 531,
    init(c) {
      ctx = c;
      c.hooks.register('bribeSergeant', () => {
        const r = ring();
        return r.ok ? { ok: true, price: r.price, level: ctx.state.heat } : { ok: false, reason: r.reason, price: r.price, level: ctx.state.heat };
      });
      offs.push(
        c.events.on('newGameStarted', () => {
          readyAt = 0;
        }),
      );
    },
    start() {
      const interactions = ctx.services.tryGet('interactions');
      const layout = ctx.services.tryGet('layout');
      if (!interactions || !layout) return;
      for (const box of layout.pois.filter((p) => p.kind === 'phonebox')) {
        offs.push(
          interactions.register({
            id: `sergeant:${box.id}`,
            pos: () => box.pos,
            radius: Math.max(3, box.radius + 1.5),
            // Beats a job ringing at the same box: while you're wanted, the call you need is this one.
            priority: 1,
            enabled: () => ctx.state.mode === 'playing' && ctx.state.heat > 0 && !online() && !ctx.services.tryGet('missions')?.active,
            prompt: () => promptFor(check()),
            onInteract: () => void ring(),
          }),
        );
      }
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
